import { useContext, useMemo } from 'react';
import { useQuery } from '@apollo/client';
import { DashboardGraph, DoughnutChartCountsCard } from '@ascd/witsby-components';
import { Box, Grid } from '@mui/material';
import { get, isEmpty } from 'lodash';
import { DateTime } from 'luxon';
import { ASCD_ID } from '@constants';
import { AppContext } from '@contexts/appContext';
import { DashboardContext, eDashboardActionType } from '@contexts/dashboardContext';
import DASHBOARD_INFO from '@graphql/schema/dashboardInfo.graphql';
import DASHBOARD_USER_COUNT_DETAILS from '@graphql/schema/dashboardUserDetailsCount.graphql';
import { DashboardInfo, IDashboardData } from '@types';
import { scrollToTop } from '@utils';
import AdminDashboardCommonCard from './CommonCard';
import { AdminDashboardLearnings } from './Learnings';
import AdminDashboardSubscriptionInfo from './SubscriptionInfo';
import AdminDashboardTopLearningStatics from './TopLearningStatics';

interface ICharts {
  id: keyof DashboardInfo;
  title?: string;
  titleIcon?: string;
  titleIconViewBox?: string;
  onTitleClick?: () => void;
}

interface ITopLearnings {
  id: keyof DashboardInfo;
  title?: string;
  countText?: string;
  onTitleClick?: () => void;
}

const AdminDashboardStatics = () => {
  const {
    state: { currentInstitution },
  } = useContext(AppContext);

  const {
    dispatch,
    state: { sideBarApiFilter },
  } = useContext(DashboardContext);

  const {
    assignedBy,
    internalId1,
    selectedInstitution,
    createdByOrganizationId,
    selectedGroup,
    dates: { startDate, endDate },
  } = sideBarApiFilter;

  // Handle institution selection filter for ASCD admins
  const currentSelectedInstitution = useMemo(() => {
    if (!isEmpty(selectedInstitution)) return selectedInstitution;

    return {
      value: currentInstitution._id,
      label: currentInstitution.name,
    };
  }, [currentInstitution._id, currentInstitution.name, selectedInstitution]);

  const variables = {
    endDate,
    startDate,
    assignedBy,
    titles: null,
    managerIds: null,
    organizationId:
      currentSelectedInstitution?.label === 'ALL' ? '' : currentSelectedInstitution.value,
    createdByOrganizationId: isEmpty(createdByOrganizationId)
      ? [currentSelectedInstitution.value, ASCD_ID]
      : createdByOrganizationId,
    schoolIds:
      selectedGroup.length > 0 ||
      currentSelectedInstitution?.label === 'ALL' ||
      currentSelectedInstitution?.label === 'ASCD'
        ? null
        : (internalId1 || []).map((internalId) => internalId?.internalId),
    groupIds: (selectedGroup || []).map((val) => val._id),
  };

  const { loading: dashboardInfoLoading, data: dashboardInfoData } = useQuery(DASHBOARD_INFO, {
    variables,
    fetchPolicy: 'network-only',
  });

  const dashboardInfo: DashboardInfo = get(dashboardInfoData, 'dashboardInfo', {});

  const { loading, data } = useQuery(DASHBOARD_USER_COUNT_DETAILS, {
    variables: {
      ...variables,
      organizationId:
        currentSelectedInstitution?.label === 'ALL' ? '' : currentSelectedInstitution.value,
      filter: {
        page: 0,
        limit: 1,
        searchText: '',
        searchField: ['userInfo.name'],
        sortDirection: 'DESC',
        sortField: 'lastAccess',
      },
    },
    fetchPolicy: 'network-only',
  });

  const dashBoardUserDetailsCount = get(data, 'dashBoardUserDetailsCount', {
    totalClockHours: 0,
    totalCoursesCount: 0,
    totalLearningItemCount: 0,
    totalStrategyCount: 0,
    totalLearningListCount: 0,
  });

  const handleChartTitleClick = (type: string) => {
    scrollToTop();
    dispatch({
      type: 'DETAILS_PAGE' as eDashboardActionType.DETAILS_PAGE,
      data: {
        type: 'COLLECTION_TYPE',
        detailsPageData: {
          selectedCollections: [type],
        },
      },
    });
  };

  const charts: ICharts[] = [
    {
      id: 'clockHours',
      title: 'Clock Hours',
      titleIcon: 'CLOCK_HOURS',
      titleIconViewBox: '0 0 27 29',
    },
    {
      id: 'wholeLearnersCount',
      title: 'Whole Learner',
      titleIcon: 'EARN_CAP',
      titleIconViewBox: '0 0 34 25',
    },
    {
      id: 'awardsEarned',
      title: 'Awards Earned',
      titleIcon: 'AWARDS_EARNED',
      titleIconViewBox: '0 0 27 29',
    },
    {
      id: 'coursesCompleted',
      title: 'Course Completion',
      titleIcon: 'COURSE_COMPLETE',
      titleIconViewBox: '0 0 32 26',
      onTitleClick: () => handleChartTitleClick('COURSES'),
    },
    {
      id: 'learningListCompleted',
      title: 'Learning List Completion',
      titleIcon: 'LEARNING_LIST',
      titleIconViewBox: '0 0 27 27',
      onTitleClick: () => handleChartTitleClick('LEARNING_LISTS'),
    },
    {
      id: 'strategyCompleted',
      title: 'Strategies Completion',
      titleIcon: 'STRATEGIES',
      titleIconViewBox: '0 0 23 31',
      onTitleClick: () => handleChartTitleClick('STRATEGY'),
    },
  ];

  const handleTopLearningsTitleClick = (selectedLearning: string) => {
    scrollToTop();
    dispatch({
      type: 'DETAILS_PAGE' as eDashboardActionType.DETAILS_PAGE,
      data: {
        type: 'TOP_LEARNINGS',
        detailsPageData: { selectedLearning },
      },
    });
  };

  const handleHighestRatedLearningsTitleClick = (selectedLearning: string) => {
    scrollToTop();
    dispatch({
      type: 'DETAILS_PAGE' as eDashboardActionType.DETAILS_PAGE,
      data: {
        type: 'HIGHEST_RATED',
        detailsPageData: { selectedLearning },
      },
    });
  };

  const topLearningsAndHighestRated: ITopLearnings[] = [
    {
      id: 'topCourses',
      title: 'Top Courses',
      countText: 'Started',
      onTitleClick: () => handleTopLearningsTitleClick('COURSES'),
    },
    {
      id: 'topLearningLists',
      title: 'Top Learning Lists',
      countText: 'Learners',
      onTitleClick: () => handleTopLearningsTitleClick('LEARNING_LISTS'),
    },
    {
      id: 'topStrategy',
      title: 'Top Strategies',
      countText: 'Started',
      onTitleClick: () => handleTopLearningsTitleClick('STRATEGY'),
    },
    {
      id: 'topEbooks',
      title: 'Top Ebooks',
      countText: 'Views',
      onTitleClick: () => handleTopLearningsTitleClick('EBOOKS'),
    },
    {
      id: 'topResources',
      title: 'Top Resources',
      countText: 'Views',
      onTitleClick: () => handleTopLearningsTitleClick('RESOURCES'),
    },
    {
      id: 'topWebinars',
      title: 'Top Webinars',
      countText: 'Views',
      onTitleClick: () => handleTopLearningsTitleClick('WEBINARS'),
    },
    {
      id: 'highestRatedCourse',
      title: 'Highest Rated Course',
      onTitleClick: () => handleHighestRatedLearningsTitleClick('COURSES'),
    },
    {
      id: 'highestRatedLearningList',
      title: 'Highest Rated Learning List',
      onTitleClick: () => handleHighestRatedLearningsTitleClick('LEARNING_LISTS'),
    },
    {
      id: 'highestRatedStrategy',
      title: 'Highest Rated Strategy',
      onTitleClick: () => handleHighestRatedLearningsTitleClick('STRATEGY'),
    },
  ];

  const handleLoginActivityTable = () => {
    scrollToTop();
    dispatch({
      type: 'DETAILS_PAGE' as eDashboardActionType.DETAILS_PAGE,
      data: {
        type: 'LOGIN_ACTIVITY',
        detailsPageData: {},
      },
    });
  };

  return (
    <Box sx={{ mt: 1, mb: 5 }}>
      <Box>
        <Grid container spacing={3}>
          <Grid item xl={12} sm={12} xs={12}>
            {/* My Subscription */}
            <AdminDashboardSubscriptionInfo
              data={dashboardInfo?.subscriptionInfo}
              loading={loading || dashboardInfoLoading}
            />
          </Grid>

          <Grid item xl={12} sm={12} xs={12}>
            {/* Login Activity */}
            <AdminDashboardCommonCard
              minHeight={336}
              title={`Login Activity from ${DateTime.fromJSDate(new Date(startDate))?.toFormat(
                `''LLLL' 'dd', 'yyyy''`,
              )} to ${DateTime.fromJSDate(new Date(endDate))?.toFormat(`''LLLL' 'dd', 'yyyy''`)}`}
              loading={loading || dashboardInfoLoading}
              onTitleClick={() => handleLoginActivityTable()}
              onExtrasClick={() => handleLoginActivityTable()}>
              <DashboardGraph
                labels={dashboardInfo?.loginActivities?.labels ?? []}
                datasets={dashboardInfo?.loginActivities?.datasets ?? []}
              />
            </AdminDashboardCommonCard>
          </Grid>

          {/* Show Learnings only if schools are selected */}
          {!isEmpty(internalId1) && <AdminDashboardLearnings />}

          {/* Statics/Chart cards */}
          {charts.map((chart) => {
            const { id, ...restChart } = chart;

            const getChartData = () => {
              if (id === 'clockHours') return dashBoardUserDetailsCount.totalClockHours;
              if (id === 'coursesCompleted') return dashBoardUserDetailsCount.totalCoursesCount;
              if (id === 'learningItemCompleted')
                return dashBoardUserDetailsCount.totalLearningItemCount;
              if (id === 'strategyCompleted') return dashBoardUserDetailsCount.totalStrategyCount;
              if (id === 'learningListCompleted')
                return dashBoardUserDetailsCount.totalLearningListCount;
              if (id === 'wholeLearnersCount') return dashboardInfo[`${id}`] || [];
              return dashboardInfo[`${id}`] as number;
            };

            return (
              <Grid item xl={4} sm={6} xs={12} sx={{ mt: 0 }} key={id}>
                <AdminDashboardCommonCard
                  {...restChart}
                  onExtrasClick={restChart.onTitleClick}
                  loading={loading || dashboardInfoLoading}>
                  <DoughnutChartCountsCard
                    id={id}
                    cutout={50}
                    width={180}
                    height={180}
                    data={getChartData()}
                    title={restChart.title}
                  />
                </AdminDashboardCommonCard>
              </Grid>
            );
          })}

          {/* Top Learning cards */}
          {topLearningsAndHighestRated.map((topLearning) => {
            const { id, countText, ...restTopLearning } = topLearning;
            return (
              <Grid item xl={4} sm={6} xs={12} sx={{ mt: 0 }} key={id}>
                <AdminDashboardCommonCard
                  minHeight={400}
                  {...restTopLearning}
                  titleIcon="DASHBOARD_TOP"
                  titleIconViewBox="0 0 27 26"
                  loading={loading || dashboardInfoLoading}>
                  <AdminDashboardTopLearningStatics
                    countText={countText}
                    onMoreClick={restTopLearning.onTitleClick}
                    data={(dashboardInfo[`${id}`] || []) as IDashboardData[]}
                  />
                </AdminDashboardCommonCard>
              </Grid>
            );
          })}
        </Grid>
      </Box>
    </Box>
  );
};

export default AdminDashboardStatics;
